"use client";

import { useMemo } from "react";
import { useUserStore } from "./_auth.store";
import { ComboboxItem } from "visuais";
import type { PageAccess, AuthData } from "@/_modulos/login";
import type {
  Filial,
  CondicaoPagamentoRow,
  CentroCustoRow,
  Grupo,
  Usuario,
} from "@apiUser";

/* =======================================================================
 * UTILITÁRIOS PARA CATÁLOGO
 * ======================================================================= */

/**
 * Lista de acesso universal para catálogo com busca dinâmica por campo.
 */
export interface ListAccessOptions<T> {
  all: T[] | undefined;
  options: ComboboxItem<string>[];
  getBy: (key: keyof T, value: string) => T | undefined;
  getByIndex: (index: number) => T | undefined;
  getOptionsByFilial?: (filial: string) => ComboboxItem<string>[];
}

/**
 * Cria um catálogo com busca por campo dinâmico, ideal para filiais, fornecedores etc.
 */
export function createListAccess<T>(
  data: T[] | undefined,
  getId: (item: T) => string,
  getLabel: (item: T) => string
): ListAccessOptions<T> {
  // Verificação de segurança para evitar erros durante prerendering
  if (!data || !Array.isArray(data)) {
    return {
      all: [],
      options: [],
      getBy: () => undefined,
      getByIndex: () => undefined,
    };
  }

  return {
    all: data,
    options: data
      .filter((item) => item != null) // Filtra itens nulos/undefined
      .map((item) => ({
        value: getId(item),
        label: getLabel(item),
      })),
    getBy: (key, value) =>
      data.find(
        (item) =>
          item != null && // Verifica se item não é null/undefined
          (item[key] ?? "").toString().toLowerCase() === value.toLowerCase()
      ),
    getByIndex: (index: number) => data[index],
  };
}

/**
 * Wrapper para retorno de item único.
 */
export interface ItemAccessOptions<T> {
  item: T | undefined;
}

export function createItemAccess<T>(data: T | undefined): ItemAccessOptions<T> {
  return { item: data };
}

/* =======================================================================
 * HOOK: useAccess - Permissões de Página
 * ======================================================================= */

/**
 * Hook para recuperar e gerenciar as permissões de acesso do usuário à página.
 * Permissões são carregadas do Zustand.
 * @returns Um objeto contendo `item` (dados PageAccess), `isLoading` e `error`.
 */
export function useAccess(): ItemAccessOptions<PageAccess> & {
  isLoading: boolean;
  error: unknown;
} {
  // Verificação de segurança para ambiente de build
  const isBuildTime = typeof window === 'undefined';
  
  // Sempre chama o hook, mas usa o valor apenas quando necessário
  const pageAccess = useUserStore((s) => s.pageAccess);

  // Como Zustand é síncrono, não há loading real
  const isLoading = false;
  const error = null;

  const itemAccess = useMemo(
    () => createItemAccess(isBuildTime ? undefined : (pageAccess || undefined)),
    [pageAccess, isBuildTime]
  );

  return {
    ...itemAccess,
    isLoading,
    error,
  };
}

/**
 * Armazena ou atualiza as permissões de acesso do usuário no Zustand.
 * @param pageAccess O objeto PageAccess a ser armazenado.
 */
export async function setUserAccess(pageAccess: PageAccess) {
  useUserStore.getState().setUserData({ pageAccess });
}

/* =======================================================================
 * HOOK: useAuth - Dados de Autenticação
 * ======================================================================= */

interface AuthHook {
  token: string | undefined;
  refreshToken: string | undefined;
  tenantId: string | undefined;
  isAuthenticated: boolean;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

/**
 * Verifica se o token ainda é válido
 */
function isTokenValid(authData: AuthData): boolean {
  return authData.expiresAt > new Date();
}

export function useAuth(): AuthHook {
  // Verificação de segurança para ambiente de build
  const isBuildTime = typeof window === 'undefined';
  
  // Sempre chama o hook, mas usa o valor apenas quando necessário
  const auth = useUserStore((s) => s.auth);

  // Como Zustand é síncrono, não há loading real
  const isLoading = false;
  const isError = false;
  const error = null;

  // Durante build time, retorna valores seguros
  if (isBuildTime) {
    return {
      token: undefined,
      refreshToken: undefined,
      tenantId: undefined,
      isAuthenticated: false,
      isLoading,
      isError,
      error,
    };
  }

  // Durante runtime, usa dados reais do store
  return {
    token: auth?.token,
    refreshToken: auth?.refreshToken,
    tenantId: auth?.tenantId,
    isAuthenticated: !!auth?.token && isTokenValid(auth),
    isLoading,
    isError,
    error,
  };
}

/* =======================================================================
 * HOOK: useCatalogs - Catálogos (Filiais, Condições, etc.)
 * ======================================================================= */

interface CatalogsHook {
  filiais: ListAccessOptions<Filial>;
  condicoes: ListAccessOptions<CondicaoPagamentoRow>;
  centroCusto: ListAccessOptions<CentroCustoRow>;
  grupos: ListAccessOptions<Grupo>;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

/**
 * Hook para acessar catálogos com busca dinâmica por qualquer campo.
 *
 * Exemplos de uso:
 *
 * // Buscar filial por código
 * const filial = filiais.getBy("codigo", "001");
 *
 * // Buscar filial por nome
 * const filial = filiais.getBy("nome", "Matriz");
 *
 * // Buscar condição por código
 * const condicao = condicoes.getBy("E4_CODIGO", "001");
 *
 * // Buscar condição por descrição
 * const condicao = condicoes.getBy("Desc", "À Vista");
 *
 * // Buscar centro de custo por código
 * const cc = centroCusto.getBy("CTT_CUSTO", "ADM");
 *
 * // Buscar centro de custo por descrição
 * const cc = centroCusto.getBy("DESC", "Administração");
 *
 * // Buscar grupo por código
 * const grupo = grupos.getBy("codigo", "ADM");
 *
 * // Buscar grupo por descrição
 * const grupo = grupos.getBy("descricao", "Administrativo");
 */
export function useCatalogs(): CatalogsHook {
  // Verificação de segurança para ambiente de build
  const isBuildTime = typeof window === 'undefined';
  
  // Sempre chama os hooks, mas usa os valores apenas quando necessário
  const filiais = useUserStore((s) => s.filiais);
  const condicoesPagamento = useUserStore((s) => s.condicoesPagamento);
  const centrosCusto = useUserStore((s) => s.centrosCusto);
  const grupos = useUserStore((s) => s.grupos);

  // Como Zustand é síncrono, não há loading real
  const isLoading = false;
  const isError = false;
  const error = null;

  // Durante build time, retorna dados seguros
  if (isBuildTime) {
    return {
      filiais: createListAccess([] as Filial[], (f) => f.codigo, (f) => `${f.codigo} - ${f.nome}`),
      condicoes: createListAccess([] as CondicaoPagamentoRow[], (c) => c.E4_CODIGO, (c) => c.E4_DESCRI),
      centroCusto: {
        all: [],
        options: [],
        getOptionsByFilial: () => [],
        getBy: () => undefined,
        getByIndex: () => undefined,
      },
      grupos: createListAccess([] as Grupo[], (g) => g.codigo, (g) => g.descricao),
      isLoading,
      isError,
      error,
    };
  }

  // Durante runtime, usa dados reais do store
  return {
    filiais: createListAccess(
      filiais,
      (f) => f.codigo,
      (f) => `${f.codigo} - ${f.nome}`
    ),
    condicoes: createListAccess(
      condicoesPagamento,
      (c) => c.E4_CODIGO,
      (c) => c.E4_DESCRI
    ),
    centroCusto: {
      all: centrosCusto
        ? centrosCusto.flatMap((filial) =>
            (filial.cc || []).map((cc) => ({
              CTT_FILIAL: filial.cttFilial,
              cc,
            }))
          )
        : [],
      options: [...(centrosCusto ? centrosCusto.flatMap((f) => f.cc || []) : [])]
        .filter((cc) => cc && cc.CTT_CUSTO && cc.CTT_DESC01)
        .map((cc) => ({
          value: cc.CTT_CUSTO,
          label: `${cc.CTT_CUSTO} - ${cc.CTT_DESC01}`,
        })),
      getOptionsByFilial: (filial: string) => {
        if (!filial || !centrosCusto) return [];
        const filialNormalizada = String(filial).trim();
        const foundExact = centrosCusto.find(
          (f) => String(f.cttFilial || "").trim() === filialNormalizada
        );
        const foundByPrefix =
          foundExact ||
          centrosCusto.find(
            (f) => String(f.cttFilial || "").trim() === filialNormalizada.slice(0, 2)
          );
        const found = foundByPrefix;
        if (!found || !Array.isArray(found.cc)) return [];
        return found.cc
          .filter((cc) => cc && cc.CTT_CUSTO && cc.CTT_DESC01)
          .map((cc) => ({
            value: cc.CTT_CUSTO,
            label: `${cc.CTT_CUSTO} - ${cc.CTT_DESC01}`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label, "pt-BR"));
      },
      getBy: (key, value) => {
        if (!centrosCusto) return undefined;
        const allCentros = centrosCusto.flatMap((filial) =>
          (filial.cc || []).map((cc) => ({
            CTT_FILIAL: filial.cttFilial,
            cc,
          }))
        );
        return allCentros.find(
          (item) =>
            (item[key] ?? "").toString().toLowerCase() === value.toLowerCase()
        );
      },
      getByIndex: (index: number) => {
        if (!centrosCusto) return undefined;
        const allCentros = centrosCusto.flatMap((filial) =>
          (filial.cc || []).map((cc) => ({
            CTT_FILIAL: filial.cttFilial,
            cc,
          }))
        );
        return allCentros[index];
      },
    },
    grupos: createListAccess(
      grupos,
      (g) => g.codigo,
      (g) => g.descricao
    ),
    isLoading,
    isError,
    error,
  };
}

/* =======================================================================
 * HOOK: useUser - Dados do Usuário
 * ======================================================================= */

/**
 * Hook to retrieve and manage user data.
 * User data is loaded from Zustand.
 * @returns An object containing user data, `isLoading`, `isError`, and `error`.
 */
export function useUser(): Usuario & {
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
} {
  // Verificação de segurança para ambiente de build
  const isBuildTime = typeof window === 'undefined';
  
  // Sempre chama o hook, mas usa o valor apenas quando necessário
  const user = useUserStore((s) => s.user);

  // Como Zustand é síncrono, não há loading real
  const isLoading = false;
  const isError = false;
  const error = null;

  // Durante build time, retorna valores seguros
  if (isBuildTime) {
    return {
      id: "",
      login: "",
      nome: "Usuário Desconhecido",
      email: undefined,
      departamento: undefined,
      cargo: undefined,
      status: "bloqueado",
      ultimoLogin: undefined,
      userDisplay: "Usuário",
      userInitials: "?",
      avatarUrl: null,
      isLoading,
      isError,
      error,
    };
  }

  const defaultUserData: Usuario = {
    id: "",
    login: "",
    nome: "Usuário Desconhecido",
    email: undefined,
    departamento: undefined,
    cargo: undefined,
    status: "bloqueado",
    ultimoLogin: undefined,
    userDisplay: "Usuário",
    userInitials: "?",
    avatarUrl: null,
  };

  return {
    ...(isBuildTime ? defaultUserData : (user ?? defaultUserData)),
    isLoading,
    isError,
    error,
  };
}

/**
 * Gets user data directly from Zustand without a hook.
 * Useful for non-React contexts or initial state hydration.
 * @returns An object containing the user data or undefined.
 */
export function getUserDataFromStore(): ItemAccessOptions<Usuario | null> {
  try {
    const data = useUserStore.getState().user;
    return createItemAccess(data);
  } catch (error) {
    console.log("Failed to get user data from Zustand:", error);
    return createItemAccess(null);
  }
}
